import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString } from '../../../locale'
import Loader from '../../../components/loader'
import ToggleSwitch from '../../../components/toggle-switch'
import classnames from 'classnames'
import WifiList from '../../../components/wifilist'
import {
    getWifiStatus,
    enableWifi,
    WifiStatusResponse,
} from '../../../api/0x0005/wifi'
import {
    getClientWifiConfig,
    updateClientWifiConfig,
    IWifiConfig,
    WifiConfigType,
    getClientWifiScanResult,
    IWifiScanResult,
} from '../../../api/0x0001/wificonfig'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    status?: WifiStatusResponse
    wifiConfig?: Record<keyof IWifiConfig, string>
    updates: Partial<IWifiConfig>
    errors: Partial<Record<keyof IWifiConfig, string>>
    unsaved: boolean
    scanResult?: IWifiScanResult[]
    scan: boolean
}

export default class WifiTab extends Component<Props, State> {
    state: State = {
        loading: true,
        updates: {},
        errors: {},
        unsaved: false,
        scan: false,
    }

    componentDidMount() {
        this.loadWifiStatus()
        this.loadWifiConfig()
    }

    loadWifiStatus = async () => {
        this.setState({ loading: true })
        try {
            const status = await getWifiStatus(this.props.clientId)
            console.log('[WiFi Tab] Received status:', status)
            console.log('[WiFi Tab] Enabled value:', status.enabled, 'type:', typeof status.enabled)
            console.log('[WiFi Tab] Connected value:', status.connected, 'type:', typeof status.connected)
            this.setState({ status, loading: false })
        } catch (error: any) {
            toast.error(error.message || getString('wifi.error'))
            this.setState({ loading: false })
        }
    }

    loadWifiConfig = async () => {
        try {
            const wifiConfig = (await getClientWifiConfig(this.props.clientId)).data
            this.setState({ wifiConfig, updates: {}, errors: {}, unsaved: false })
        } catch (err) {
            console.error(err)
        }
    }

    // Helper to determine if WiFi is actually enabled (ESP32 firmware bug workaround)
    isWifiEnabled = () => {
        const { status } = this.state
        if (!status) return false
        // If connected or has IP address, WiFi must be enabled
        return status.enabled === 1 || status.connected === 1 || !!status.ipAddress
    }

    handleEnableToggle = async () => {
        if (!this.state.status) return

        const currentlyEnabled = this.isWifiEnabled()
        const newEnabledState = !currentlyEnabled
        try {
            await enableWifi(this.props.clientId, newEnabledState)
            toast.success(
                newEnabledState
                    ? getString('wifi.wifi_enabled')
                    : getString('wifi.wifi_disabled')
            )
            await this.loadWifiStatus()
        } catch (error: any) {
            toast.error(error.message || getString('wifi.error'))
        }
    }

    async scan() {
        this.setState({ scan: true, scanResult: undefined }, async () => {
            try {
                const scanResult = (await getClientWifiScanResult(this.props.clientId)).data
                this.setState({ scanResult })
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
            }
        })
    }

    async saveWifiConfig() {
        try {
            const { updates, errors } = this.state
            if (Object.keys(updates).length === 0) {
                return toast.error(getString('settings.no_updates'))
            }
            if (Object.keys(errors).length > 0) {
                return toast.error(getString('settings.fix_errors'))
            }
            await updateClientWifiConfig(
                this.props.clientId,
                Object.entries(updates).map(([key, value]) => ({
                    type: WifiConfigType[key as any] as any,
                    value: value!,
                })),
            )
            await this.loadWifiConfig()
            toast.success(getString('common.success', 'common.saved'))
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    handleChange(name: keyof IWifiConfig, value: string) {
        const { wifiConfig, updates, errors } = this.state
        wifiConfig![name] = value
        updates[name] = value
        delete errors[name]
        this.setState({ wifiConfig, updates, unsaved: true })
    }

    render() {
        const { loading, status, wifiConfig } = this.state

        if (loading || !status) {
            return (
                <div className="flex justify-center items-center h-64">
                    <Loader />
                </div>
            )
        }

        return (
            <div className="p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">{getString('wifi.title')}</h2>

                {/* Enable/Disable Toggle */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('wifi.enable')}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {status.enabled === 1
                                    ? getString('wifi.enabled')
                                    : getString('wifi.disabled')}
                            </p>
                        </div>
                        <ToggleSwitch
                            checked={status.enabled === 1}
                            onChange={this.handleEnableToggle}
                        />
                    </div>
                </div>

                {/* Status Card */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('wifi.status')}</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">{getString('common.status')}:</span>
                            <span className={status.connected === 1 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}>
                                {status.connected === 1
                                    ? getString('wifi.connected')
                                    : getString('wifi.not_connected')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Network Information Card */}
                {status.enabled === 1 && (
                    <>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                            <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('wifi.ssid')}</h3>
                            <div className="text-gray-800 dark:text-gray-200 font-medium">{status.ssid || 'N/A'}</div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                            <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('wifi.ip_address')}</h3>
                            <div className="text-gray-800 dark:text-gray-200 font-mono">{status.ipAddress || 'N/A'}</div>
                        </div>
                    </>
                )}

                {/* WiFi Configuration */}
                {wifiConfig && (
                    <>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
                            <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('wificonfig.sta_settings')}</h3>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {getString('wificonfig.sta_ssid')}
                                </label>
                                <input
                                    className={classnames('pretty-input ltr', this.state.errors.staSsid ? 'border-red-500' : 'border-gray-300')}
                                    value={wifiConfig.staSsid}
                                    onInput={(e: any) => this.handleChange('staSsid', e.target.value)}
                                />
                                {this.state.errors.staSsid && (
                                    <div className="text-red-500 text-sm mt-1">{this.state.errors.staSsid}</div>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {getString('wificonfig.sta_pass')}
                                </label>
                                <input
                                    className={classnames('pretty-input ltr', this.state.errors.staPass ? 'border-red-500' : 'border-gray-300')}
                                    value={wifiConfig.staPass}
                                    onInput={(e: any) => this.handleChange('staPass', e.target.value)}
                                />
                                {this.state.errors.staPass && (
                                    <div className="text-red-500 text-sm mt-1">{this.state.errors.staPass}</div>
                                )}
                            </div>

                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">{getString('wificonfig.sta_warning')}</p>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => this.saveWifiConfig()}
                                    className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    {getString('common.save')}
                                </button>
                                <button
                                    onClick={() => this.loadWifiConfig()}
                                    className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                >
                                    {getString('settings.refresh')}
                                </button>
                                <button
                                    onClick={() => this.scan()}
                                    className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                    {getString('common.scan')}
                                </button>
                                {this.state.unsaved && (
                                    <div className="flex items-center text-gray-700 dark:text-gray-300 text-sm">
                                        * {getString('common.unsaved_changes')}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* WiFi Scan Results */}
                        {this.state.scan && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">{getString('wificonfig.scan')}</h3>
                                <WifiList
                                    items={this.state.scanResult}
                                    onSsidChange={ssid => this.handleChange('staSsid', ssid)}
                                    onPassChange={pass => this.handleChange('staPass', pass || '')}
                                    password={wifiConfig.staPass}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        )
    }
}
